import { ComponentStore } from '@ngrx/component-store';
import { inject, Injectable } from '@angular/core';
import { catchError, concatMap } from 'rxjs/operators';
import { EMPTY, Observable, of } from 'rxjs';
import equal from 'fast-deep-equal';
import { IGcComponentStore } from '@app/core/interfaces/gc-component-store.interface';
import { FLOATING_POPUP_DATA } from '@app/main/main-content/floating-popup-manager/providers/floating-popup-data.token';
import { Store } from '@ngrx/store';
import { selectMainProjectId } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { LoggerDeploymentPicturesApiService } from '@app/api/services/logger-deployment-pictures-api.service';
import {
  IFpLoggerNoisePicturesState,
  initialFpLoggerNoisePicturesState,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-pictures/services/fp-logger-noise-pictures/fp-logger-noise-pictures.state';
import {
  FpLoggerNoisePicturesActionGetData,
  FpLoggerNoisePicturesActionGetDataError,
  FpLoggerNoisePicturesActionGetDataSuccesses,
  FpLoggerNoisePicturesActions,
  FpLoggerNoisePicturesActionType,
  FpLoggerNoisePicturesActionUpdatePicture,
  FpLoggerNoisePicturesActionUpdatePictureError,
  FpLoggerNoisePicturesActionUpdatePictureSuccess,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-pictures/services/fp-logger-noise-pictures/fp-logger-noise-pictures.actions';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';

@Injectable()
export class FpLoggerNoisePicturesStore
  extends ComponentStore<IFpLoggerNoisePicturesState>
  implements IGcComponentStore<FpLoggerNoisePicturesActions>
{
  public readonly data = inject<ILdLogger>(FLOATING_POPUP_DATA);
  protected readonly store = inject(Store);
  protected readonly loggerDeploymentPicturesApiService = inject(LoggerDeploymentPicturesApiService);

  constructor() {
    super(initialFpLoggerNoisePicturesState);
  }

  public readonly date$ = this.select(s => s.date, { equal });
  public readonly popupMainData$ = this.select(s => s.popupMainData, { equal });
  public readonly data$ = this.select(s => s.data, { equal });
  public readonly error$ = this.select(s => s.error, { equal });
  public readonly loading$ = this.select(s => s.loading, { equal });
  public readonly pictureUpdateLoading$ = this.select(s => s.pictureUpdateLoading, { equal });

  private mainProjectId = this.store.selectSignal(selectMainProjectId);

  public dispatch = this.effect((action$: Observable<FpLoggerNoisePicturesActions>) =>
    action$.pipe(
      concatMap(action => {
        switch (action.type) {
          case FpLoggerNoisePicturesActionType.GetData: {
            this.reducerGetData();
            this.effectGetData(action);
            break;
          }
          case FpLoggerNoisePicturesActionType.GetDataSuccesses: {
            this.reducerGetDataSuccesses(action);
            break;
          }
          case FpLoggerNoisePicturesActionType.GetDataError: {
            this.reducerGetDataError(action);
            break;
          }
          case FpLoggerNoisePicturesActionType.UpdatePicture: {
            this.reducerUpdatePicture();
            this.effectUpdatePicture(action);
            break;
          }
          case FpLoggerNoisePicturesActionType.UpdatePictureSuccess: {
            this.reducerUpdatePictureSuccess(action);
            break;
          }
          case FpLoggerNoisePicturesActionType.UpdatePictureError: {
            this.reducerUpdatePictureError(action);
            break;
          }
        }
        return of(action);
      })
    )
  );

  private reducerGetData() {
    this.patchState({ loading: true, error: null });
  }

  private reducerGetDataSuccesses(action: FpLoggerNoisePicturesActionGetDataSuccesses) {
    this.patchState({
      date: action.date,
      popupMainData: action.popupMainData,
      data: action.data,
      loading: false,
    });
  }

  private reducerGetDataError(action: FpLoggerNoisePicturesActionGetDataError) {
    this.patchState({
      date: action.date,
      popupMainData: action.popupMainData,
      data: null,
      error: action.error,
      loading: false,
    });
  }

  private reducerUpdatePicture() {
    this.patchState({ pictureUpdateLoading: true, error: null });
  }

  private reducerUpdatePictureSuccess(action: FpLoggerNoisePicturesActionUpdatePictureSuccess) {
    const state = this.get();

    if (!state.data) {
      this.patchState({ pictureUpdateLoading: false });
      return;
    }

    this.patchState({
      pictureUpdateLoading: false,
      error: null,
      data: state.data.map(picture => (picture.id === action.picture.id ? action.picture : picture)),
    });
  }

  private reducerUpdatePictureError(action: FpLoggerNoisePicturesActionUpdatePictureError) {
    this.patchState({
      pictureUpdateLoading: false,
      error: action.error,
    });
  }

  private effectGetData(action: FpLoggerNoisePicturesActionGetData) {
    const mainProjectId = this.mainProjectId();
    const deployment = action.popupMainData?.deployment;

    if (!mainProjectId || !deployment) {
      this.dispatch({
        type: FpLoggerNoisePicturesActionType.GetDataSuccesses,
        date: action.date,
        popupMainData: action.popupMainData,
        data: null,
      });
      return;
    }

    const state = this.get();

    const isOldDataValid = state.data && state.popupMainData && state.popupMainData.deployment?.id === deployment.id;

    if (isOldDataValid) {
      this.dispatch({
        type: FpLoggerNoisePicturesActionType.GetDataSuccesses,
        date: action.date,
        popupMainData: action.popupMainData,
        data: state.data,
      });
      return;
    }

    this.loggerDeploymentPicturesApiService
      .getAll(mainProjectId, deployment.id)
      .pipe(
        catchError(error => {
          this.dispatch({
            type: FpLoggerNoisePicturesActionType.GetDataError,
            date: action.date,
            popupMainData: action.popupMainData,
            error,
          });
          return EMPTY;
        })
      )
      .subscribe(({ data }) => {
        this.dispatch({
          type: FpLoggerNoisePicturesActionType.GetDataSuccesses,
          date: action.date,
          popupMainData: action.popupMainData,
          data,
        });
      });
  }

  private effectUpdatePicture(action: FpLoggerNoisePicturesActionUpdatePicture) {
    const state = this.get();
    const projectId = this.mainProjectId();
    const deploymentId = state.popupMainData?.deployment?.id;

    if (!projectId || !deploymentId) {
      this.patchState({ pictureUpdateLoading: false });

      return;
    }

    this.loggerDeploymentPicturesApiService
      .update(projectId, deploymentId, action.pictureId, action.update)
      .pipe(
        catchError(error => {
          this.dispatch({
            type: FpLoggerNoisePicturesActionType.UpdatePictureError,
            error,
          });

          return EMPTY;
        })
      )
      .subscribe(response => {
        this.dispatch({
          type: FpLoggerNoisePicturesActionType.UpdatePictureSuccess,
          picture: response.data,
        });
      });
  }
}
